import { z } from 'zod';

/** A rodoviária como a busca de ônibus a conhece: o slug do bus-api (`code`) e o
 *  nome que a pessoa escolheu — o mesmo par das buscas recentes de ônibus. */
const BusPreferredStopSchema = z
  .object({
    code: z.string().min(1).max(120),
    name: z.string().trim().min(1).max(120),
  })
  .strict();

/** A viação como o catálogo do bus-api a conhece (`busCompanyList`): o
 *  `externalCode` do catálogo e o nome. O código do catálogo nem sempre é o
 *  código da viação nas viagens (catálogo e busca podem vir de provedores
 *  diferentes), então o nome também serve para casar a preferência. */
const BusPreferredCarrierSchema = z
  .object({
    code: z.string().min(1).max(120),
    name: z.string().trim().min(1).max(80),
  })
  .strict();

/** Janela ou corredor — a posição da poltrona na fileira. */
const BusSeatRowSchema = z.enum(['window', 'aisle']);

/** O terço do ônibus: frente, meio ou fundo (as fileiras de cada andar). */
const BusSeatAreaSchema = z.enum(['front', 'middle', 'back']);

/** O período da partida — com a madrugada: ônibus viaja de noite. */
const BusDepartureWindowSchema = z.enum(['dawn', 'morning', 'afternoon', 'evening']);

/**
 * As preferências de ÔNIBUS do viajante — declaradas por ele na aba "Ônibus"
 * das Preferências, não mineradas das viagens (essas são as
 * `TravelerTravelPreferences`). O legado não tem o campo, então ele vive num
 * store do customer-api (`bus_preferences`) — a exceção sancionada em
 * "Profile truth". `null` = sem preferência.
 *
 * - `carrier`: a viação do catálogo do bus-api (código + nome).
 * - `seatClass`: o NOME da classe como o bus-api a devolve na oferta
 *   (`seatClass`) — é por ele que a lista de ônibus a agrupa.
 * - `departureWindow`: o período da partida.
 * - `preferDirect`: viagens diretas antes das com conexão.
 * - `seatRow`, `seatArea`: a posição da poltrona na fileira e no ônibus
 *   (frente, meio, fundo).
 * - `seatTogether`: com mais de um viajante, poltronas lado a lado.
 * - `homeStop`: a rodoviária de origem de costume.
 *
 * Wire de leitura E de escrita (`PUT` substitui o documento inteiro). Os campos
 * que chegaram depois têm `default`: um documento gravado antes deles lê como
 * "sem preferência".
 */
export const BusPreferencesSchema = z
  .object({
    carrier: BusPreferredCarrierSchema.nullable(),
    seatClass: z.string().trim().min(1).max(40).nullable(),
    departureWindow: BusDepartureWindowSchema.nullable().default(null),
    preferDirect: z.boolean().default(false),
    seatRow: BusSeatRowSchema.nullable(),
    seatArea: BusSeatAreaSchema.nullable().default(null),
    seatTogether: z.boolean(),
    homeStop: BusPreferredStopSchema.nullable(),
  })
  .strict();
export type BusPreferences = z.infer<typeof BusPreferencesSchema>;

/** Quem nunca salvou nada: nenhuma preferência, e juntos quando viajam juntos. */
export const DEFAULT_BUS_PREFERENCES: BusPreferences = {
  carrier: null,
  seatClass: null,
  departureWindow: null,
  preferDirect: false,
  seatRow: null,
  seatArea: null,
  seatTogether: true,
  homeStop: null,
};
